import { useEffect, useState } from "react";
import { Cookie } from "lucide-react";
import {
  CONSENT_COOKIE_DOMAIN,
  getStoredConsentChoice,
  storeConsentChoice,
  updateConsent,
  type ConsentChoice,
} from "@/lib/consent";

const ACCENT = "#5c23ff";
const PRODUCT_NAME = "QRHUB";
const PRIVACY_POLICY_URL = "https://qrhub.io/en/legal/privacy-policy/";

/**
 * Full-width bottom-sheet Consent Mode banner (Accept Cookies / Necessary Only).
 * Design matches the shared getmeno/dashboard consent banner, with QRHUB branding.
 */
export function ConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    setVisible(getStoredConsentChoice() === null);
  }, []);

  // Slide-up entrance after mount so the first paint isn't already at rest.
  useEffect(() => {
    if (!visible) {
      setEntered(false);
      return;
    }
    const id = window.requestAnimationFrame(() => {
      setEntered(true);
    });
    return () => {
      window.cancelAnimationFrame(id);
    };
  }, [visible]);

  if (!visible) return null;

  const choose = (choice: ConsentChoice): void => {
    storeConsentChoice(choice, { cookieDomain: CONSENT_COOKIE_DOMAIN });
    updateConsent(choice);
    setVisible(false);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 pointer-events-none"
      role="dialog"
      aria-modal="false"
      aria-labelledby="consent-banner-title"
      aria-describedby="consent-banner-description"
    >
      <div
        className={[
          "pointer-events-auto relative w-full bg-white shadow-[0_-8px_40px_rgba(0,0,0,0.12)]",
          "border-t-[6px]",
          "transition-transform duration-300 ease-out",
          entered ? "translate-y-0" : "translate-y-full",
        ].join(" ")}
        // style={{ borderTopColor: ACCENT }}
      >
        {/* Overlapping brand icon — half above the colored top border */}
        <div
          className="absolute -top-7 left-5 sm:left-8 z-10 flex size-14 sm:size-16 items-center justify-center rounded-full shadow-lg ring-4 ring-white"
          style={{ backgroundColor: ACCENT }}
          aria-hidden
        >
          <Cookie className="text-white size-6 sm:size-7" strokeWidth={2} />
        </div>

        <div className="mx-auto max-w-5xl px-4 sm:px-8 lg:px-10 py-6 sm:py-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:gap-10 my-10">
            <div className="flex-1 min-w-0 pt-8 sm:pt-6 lg:pt-2 sm:pl-20">
              <h2
                id="consent-banner-title"
                className="text-xl sm:text-2xl font-bold text-neutral-900 m-0 mb-2"
              >
                We care about your privacy
              </h2>
              <p
                id="consent-banner-description"
                className="text-sm sm:text-base text-neutral-600 m-0 leading-relaxed"
              >
                We use cookies and similar technologies to understand how you
                use {PRODUCT_NAME}, measure performance, and improve your
                experience. <br /> Check our{" "}
                <a
                  href={PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold underline underline-offset-2"
                  style={{ color: ACCENT }}
                >
                  Privacy Policy
                </a>
              </p>
            </div>

            <div className="flex flex-col sm:flex-row lg:flex-col gap-3 shrink-0 w-full sm:w-auto lg:w-56">
              <button
                type="button"
                className="w-full justify-center text-base font-semibold px-6 min-h-12 inline-flex items-center rounded-xl text-white transition hover:opacity-95 active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                style={{
                  backgroundColor: ACCENT,
                  outlineColor: ACCENT,
                }}
                onClick={() => {
                  choose("granted");
                }}
              >
                Accept cookies
              </button>
              <button
                type="button"
                className="w-full justify-center text-base font-semibold px-6 min-h-12 inline-flex items-center rounded-xl border-2 border-neutral-300 bg-transparent text-neutral-900 transition hover:border-[#5c23ff] hover:text-[#5c23ff] active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5c23ff]"
                onClick={() => {
                  choose("necessary_only");
                }}
              >
                Necessary only
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
